import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import styles from './LoanTracker.module.css';

type LoanRole = 'lender' | 'borrower' | 'reader';
type LoanId = 'earnest' | 'nagu';

const LOANS: { id: LoanId; label: string }[] = [
  { id: 'earnest', label: 'Earnest' },
  { id: 'nagu', label: 'Nagu' },
];

type RoleUser = {
  email: string;
  superuser: boolean;
  loanRoles: Partial<Record<LoanId, LoanRole>>;
};

type Draft = { superuser: boolean; loanRoles: Partial<Record<LoanId, LoanRole>> };

function LoanRoleSelects({
  value,
  disabled,
  onChange,
}: {
  value: Partial<Record<LoanId, LoanRole>>;
  disabled: boolean;
  onChange: (loanId: LoanId, role: LoanRole | null) => void;
}) {
  return (
    <div className={styles.checkboxRow}>
      {LOANS.map((loan) => (
        <label key={loan.id} className={styles.checkboxLabel}>
          {loan.label}
          <select
            value={value[loan.id] ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(loan.id, (e.target.value || null) as LoanRole | null)}
          >
            <option value="">No access</option>
            <option value="lender">Lender</option>
            <option value="borrower">Borrower</option>
            <option value="reader">Reader</option>
          </select>
        </label>
      ))}
    </div>
  );
}

function setLoanRole(
  loanRoles: Partial<Record<LoanId, LoanRole>>,
  loanId: LoanId,
  role: LoanRole | null
): Partial<Record<LoanId, LoanRole>> {
  const next = { ...loanRoles };
  if (role === null) {
    delete next[loanId];
  } else {
    next[loanId] = role;
  }
  return next;
}

// Superuser-only: lets a superuser grant, change, or revoke anyone's role
// per loan, instead of running scripts by hand. Writes go straight to
// Firestore — firestore.rules is what actually checks the caller is a
// superuser before allowing any of this.
const AdminPanel: React.FC = () => {
  const myEmail = auth.currentUser?.email ?? null;
  const [users, setUsers] = useState<RoleUser[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [newEmail, setNewEmail] = useState('');
  const [newSuperuser, setNewSuperuser] = useState(false);
  const [newLoanRoles, setNewLoanRoles] = useState<Partial<Record<LoanId, LoanRole>>>({});

  useEffect(() => {
    return onSnapshot(collection(db, 'roles'), (snapshot) => {
      const list: RoleUser[] = snapshot.docs.map((d) => ({
        email: d.id,
        superuser: d.data().superuser === true,
        loanRoles: (d.data().loanRoles ?? {}) as Partial<Record<LoanId, LoanRole>>,
      }));
      list.sort((a, b) => a.email.localeCompare(b.email));
      setUsers(list);

      // Only seed a draft the first time we see a user, so in-progress
      // edits aren't clobbered by the live listener.
      setDrafts((prev) => {
        const next = { ...prev };
        list.forEach((u) => {
          if (!next[u.email]) {
            next[u.email] = { superuser: u.superuser, loanRoles: u.loanRoles };
          }
        });
        return next;
      });
    });
  }, []);

  const updateDraft = (email: string, patch: Partial<Draft>) => {
    setDrafts((prev) => ({ ...prev, [email]: { ...prev[email], ...patch } }));
  };

  const saveUser = (email: string) => {
    const draft = drafts[email];
    if (!draft) return;
    setDoc(doc(db, 'roles', email), {
      superuser: draft.superuser,
      loanRoles: draft.superuser ? {} : draft.loanRoles,
    }).catch((err) => console.error('Failed to save role', err));
  };

  const removeUser = (email: string) => {
    if (!window.confirm(`Revoke all access for ${email}?`)) return;
    deleteDoc(doc(db, 'roles', email)).catch((err) => console.error('Failed to remove role', err));
  };

  const addUser = (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) return;

    setDoc(doc(db, 'roles', email), {
      superuser: newSuperuser,
      loanRoles: newSuperuser ? {} : newLoanRoles,
    })
      .then(() => {
        setNewEmail('');
        setNewSuperuser(false);
        setNewLoanRoles({});
      })
      .catch((err) => console.error('Failed to add user', err));
  };

  return (
    <div className={styles.ledgerPanel}>
      <h2 className={styles.panelTitle}>Admin</h2>

      <form className={styles.adminForm} onSubmit={addUser}>
        <label className={styles.field}>
          Email
          <input
            type="email"
            placeholder="someone@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
        </label>
        <label className={styles.checkboxLabel}>
          <input type="checkbox" checked={newSuperuser} onChange={(e) => setNewSuperuser(e.target.checked)} />
          Superuser
        </label>
        {!newSuperuser && (
          <LoanRoleSelects
            value={newLoanRoles}
            disabled={false}
            onChange={(loanId, role) => setNewLoanRoles((prev) => setLoanRole(prev, loanId, role))}
          />
        )}
        <button type="submit" className={`${styles.button} ${styles.accentButton}`}>
          Add
        </button>
      </form>

      <div className={styles.ledgerScroll}>
        <table className={styles.ledgerTable}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Superuser</th>
              <th>Accounts</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.ledgerEmpty}>
                  No users yet.
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const draft = drafts[u.email] ?? { superuser: u.superuser, loanRoles: u.loanRoles };
                const isSelf = u.email === myEmail;
                return (
                  <tr key={u.email}>
                    <td>
                      {u.email}
                      {isSelf && <em> (you)</em>}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={draft.superuser}
                        disabled={isSelf}
                        onChange={(e) => updateDraft(u.email, { superuser: e.target.checked })}
                      />
                    </td>
                    <td>
                      {!draft.superuser && (
                        <LoanRoleSelects
                          value={draft.loanRoles}
                          disabled={isSelf}
                          onChange={(loanId, role) =>
                            updateDraft(u.email, { loanRoles: setLoanRole(draft.loanRoles, loanId, role) })
                          }
                        />
                      )}
                    </td>
                    <td>
                      {!isSelf && (
                        <div className={styles.adminActions}>
                          <button
                            className={`${styles.button} ${styles.accentButton} ${styles.smallButton}`}
                            onClick={() => saveUser(u.email)}
                          >
                            Save
                          </button>
                          <button
                            className={`${styles.button} ${styles.dangerButton} ${styles.smallButton}`}
                            onClick={() => removeUser(u.email)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPanel;
